import React from 'react'
import { useNewTranslation } from 'hooks'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import { TASK_TYPE_ENUM } from 'services/api/constants'
import { useDispatch } from 'react-redux'
import { setConfirmPopup } from 'state/confirmPopup'
import { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import { IconName } from 'particles/Icon'
import RichContentMenu from 'particles/Menu/RichContentMenu'
import MenuItem from 'particles/MenuItem'

interface TaskTypeSelectorProps {
    campaignStatus: CAMPAIGN_STATUS_ENUM | undefined
    createTask: Function // eslint-disable-line @typescript-eslint/ban-types
    onHideSelector: () => void
    anchorEl: HTMLElement | null
}

interface MenuItemProps {
    title: string
    description?: string
    icon: IconName
    onClick: () => void
    testId?: string
}

export interface MenuSectionProps {
    title: string
    menuItems: MenuItemProps[]
}

const TaskTypeSelector: React.FC<TaskTypeSelectorProps> = ({
    campaignStatus,
    createTask,
    onHideSelector,
    anchorEl,
}) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const handleClick = (taskTypeName: TASK_TYPE_ENUM): void => {
        const isCampaignEditorValueChange = localStorage.getItem(RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE)
        if (isCampaignEditorValueChange) {
            dispatch(
                setConfirmPopup({
                    show: true,
                    onConfirm: () => {
                        createTask(taskTypeName)
                        onHideSelector()
                    },
                }),
            )
        } else {
            createTask(taskTypeName)
            onHideSelector()
        }
    }

    if (campaignStatus !== CAMPAIGN_STATUS_ENUM.DRAFT && campaignStatus !== CAMPAIGN_STATUS_ENUM.FOR_EDIT) {
        return null
    }

    const taskListSections: MenuSectionProps[] = [
        {
            title: t('campaignEditor.createTask.actionsMenuHeader'),
            menuItems: [
                {
                    title: t('taskType.purchaseProduct'),
                    description: t('campaignEditor.createTask.productPurchaseMenuDescription'),
                    icon: 'shoppingCart',
                    onClick: (): void => handleClick(TASK_TYPE_ENUM.PURCHASE_PRODUCT),
                    testId: `test-menu-item-${TASK_TYPE_ENUM.PURCHASE_PRODUCT}`,
                },
                {
                    title: t('taskType.attendEvent'),
                    description: t('campaignEditor.createTask.attendEventMenuDescription'),
                    icon: 'calendarCheck',
                    onClick: (): void => handleClick(TASK_TYPE_ENUM.ATTEND_EVENT),
                    testId: `test-menu-item-${TASK_TYPE_ENUM.ATTEND_EVENT}`,
                },
            ],
        },
        {
            title: t('campaignEditor.createTask.instagramMenuHeader'),
            menuItems: [
                {
                    title: t('taskType.instagramPost'),
                    description: t('campaignEditor.createTask.instagramPostMenuDescription'),
                    icon: 'instagramLogo',
                    onClick: (): void => handleClick(TASK_TYPE_ENUM.IG_POST_V02),
                    testId: `test-menu-item-${TASK_TYPE_ENUM.IG_POST_V02}`,
                },
                {
                    title: t('taskType.instagramStory'),
                    description: t('campaignEditor.createTask.instagramStoryMenuDescription'),
                    icon: 'instagramLogo',
                    onClick: (): void => handleClick(TASK_TYPE_ENUM.IG_STORY_V02),
                    testId: `test-menu-item-${TASK_TYPE_ENUM.IG_STORY_V02}`,
                },
                {
                    title: t('taskType.instagramReel'),
                    description: t('campaignEditor.createTask.instagramReelMenuDescription'),
                    icon: 'instagramLogo',
                    onClick: (): void => handleClick(TASK_TYPE_ENUM.IG_REEL),
                    testId: `test-menu-item-${TASK_TYPE_ENUM.IG_REEL}`,
                },
            ],
        },
        {
            title: t('campaignEditor.createTask.facebookMenuHeader'),
            menuItems: [
                {
                    title: t('taskType.facebook'),
                    description: t('campaignEditor.createTask.facebookPostMenuDescription'),
                    icon: 'facebookLogo',
                    onClick: (): void => handleClick(TASK_TYPE_ENUM.FB_POST_V02),
                    testId: `test-menu-item-${TASK_TYPE_ENUM.FB_POST_V02}`,
                },
            ],
        },
        {
            title: t('campaignEditor.createTask.tiktokMenuHeader'),
            menuItems: [
                {
                    title: t('taskType.tikTok'),
                    description: t('campaignEditor.createTask.tiktokMenuDescription'),
                    icon: 'tiktokLogo',
                    onClick: (): void => handleClick(TASK_TYPE_ENUM.TIKTOK_POST),
                    testId: `test-menu-item-${TASK_TYPE_ENUM.TIKTOK_POST}`,
                },
            ],
        },
        {
            title: t('campaignEditor.createTask.xiaohongshuMenuHeader'),
            menuItems: [
                {
                    title: t('taskType.xiaohongshuPost'),
                    description: t('campaignEditor.createTask.xiaohongshuMenuDescription'),
                    icon: 'xiaohongshuLogo',
                    onClick: (): void => handleClick(TASK_TYPE_ENUM.XIAOHONGSHU_POST),
                    testId: `test-menu-item-${TASK_TYPE_ENUM.XIAOHONGSHU_POST}`,
                },
            ],
        },
        {
            title: t('campaignEditor.createTask.othersMenuHeader'),
            menuItems: [
                {
                    title: t('taskType.customTask'),
                    description: t('campaignEditor.createTask.customTaskMenuDescription'),
                    icon: 'calendarCheck',
                    onClick: (): void => handleClick(TASK_TYPE_ENUM.CUSTOM),
                    testId: `test-menu-item-${TASK_TYPE_ENUM.CUSTOM}`,
                },
            ],
        },
    ]

    const open = Boolean(anchorEl)

    return (
        <RichContentMenu anchorEl={anchorEl} open={open} onClose={onHideSelector}>
            {taskListSections?.map((section, index) => (
                <>
                    {index > 0 ? <MenuItem.Divider /> : null}
                    <div key={index} className="menu-section">
                        {section.title && <MenuItem.Title text={section.title} size="s" case="uppercase" />}
                        {section.menuItems.map((item, sectionIndex) => (
                            <MenuItem.Text
                                key={`menu-item-${sectionIndex}`}
                                text={item.title}
                                dataTestId={item.testId}
                                description={item.description}
                                icon={item.icon}
                                tabIndex={sectionIndex}
                                onClick={item.onClick}
                                isRichContentItem
                            />
                        ))}
                    </div>
                </>
            ))}
        </RichContentMenu>
    )
}
export default TaskTypeSelector
